/**
 * Het model-portal: inloggen, uitnodiging gebruiken, en de eigen cijfers.
 *
 * Fase 2 (go Jordy 02-10-2026; eisen in docs/requirements/model-portal.md).
 * Draait onder /portal, los van het dashboard: geen menu, eigen token. Een model
 * ziet alleen haar eigen verkopen en aandeel, zonder chatter- of fannaam, en de
 * uitleg dat uitbetalingen via het platform lopen (§4.1).
 */
import { useEffect, useMemo, useState } from 'react'
import { ErrorNote, Loading, Panel, Table } from '../components/ui'
import { instanceConfig } from '../lib/instance'
import { eur } from '../lib/format'
import { LineChartPanel } from '../components/charts'
import {
  downloadExport, portalToken, portalVraag, PortalFout, wisPortalToken, zetPortalToken,
} from './portalApi'

interface Ik {
  name: string; email: string; two_factor_enabled: boolean; platforms: string[]; payout_note: string
  /** 04-10-2026: Earnings gaat pas open na akkoord van de eigenaar (schakelaar in Settings). */
  earnings_enabled: boolean
}
interface Regel { date: string; platform: string; type: string; gross: number; your_share_pct: number | null; your_share: number | null }
interface PerPlatform { sales: number; gross: number; your_share: number | null; share_pct: number | null; share_unknown: number }

const PLATFORM_NAAM: Record<string, string> = { fancentro: 'FanCentro', f2f: 'F2F' }

function Kaart({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div className="card" style={{ width: 400, maxWidth: '94vw' }}>{children}</div>
    </div>
  )
}

function Kop() {
  const merk = instanceConfig()
  return (
    <div className="row" style={{ marginBottom: 14 }}>
      <div className="logo">{merk.initials}</div>
      <div>
        <b>{merk.brand}</b>
        <div className="mut2" style={{ fontSize: 12 }}>Model portal</div>
      </div>
    </div>
  )
}

function Inloggen({ onIn }: { onIn: () => void }) {
  const [email, setEmail] = useState('')
  const [wachtwoord, setWachtwoord] = useState('')
  const [code, setCode] = useState('')
  const [codeNodig, setCodeNodig] = useState(false)
  const [fout, setFout] = useState('')
  const [bezig, setBezig] = useState(false)
  async function verstuur(e: React.FormEvent) {
    e.preventDefault(); setFout(''); setBezig(true)
    try {
      const r = await portalVraag<{ token: string }>('/api/model/auth/login', {
        method: 'POST', body: JSON.stringify({ email, password: wachtwoord, code: code || null }),
      })
      zetPortalToken(r.token); onIn()
    } catch (err) {
      if (err instanceof PortalFout && err.message === 'totp_required') { setCodeNodig(true); setFout('') }
      else setFout(err instanceof Error ? err.message : String(err))
    } finally { setBezig(false) }
  }
  return (
    <Kaart>
      <Kop />
      <form onSubmit={verstuur}>
        <div className="field"><label>Email</label>
          <input className="inp" type="email" value={email} autoComplete="username" onChange={(e) => setEmail(e.target.value)} /></div>
        <div className="field"><label>Password</label>
          <input className="inp" type="password" value={wachtwoord} autoComplete="current-password"
                 onChange={(e) => setWachtwoord(e.target.value)} /></div>
        {codeNodig ? (
          <div className="field"><label>Authentication code</label>
            <input className="inp" inputMode="numeric" autoComplete="one-time-code" value={code}
                   onChange={(e) => setCode(e.target.value)} /></div>
        ) : null}
        {fout ? <div className="note" style={{ marginBottom: 8 }}>{fout}</div> : null}
        <button className="btn primary" type="submit" disabled={bezig || !email || !wachtwoord} style={{ width: '100%' }}>
          Sign in
        </button>
      </form>
    </Kaart>
  )
}

function Uitnodiging({ sleutel, onKlaar }: { sleutel: string; onKlaar: () => void }) {
  const [email, setEmail] = useState<string | null>(null)
  const [fout, setFout] = useState('')
  const [w1, setW1] = useState('')
  const [w2, setW2] = useState('')
  const [bezig, setBezig] = useState(false)
  useEffect(() => {
    portalVraag<{ email: string }>(`/api/model/auth/invite/${encodeURIComponent(sleutel)}`)
      .then((r) => setEmail(r.email)).catch((e) => setFout(e instanceof Error ? e.message : String(e)))
  }, [sleutel])
  async function verstuur(e: React.FormEvent) {
    e.preventDefault(); setFout('')
    if (w1 !== w2) { setFout('The passwords do not match.'); return }
    setBezig(true)
    try {
      await portalVraag(`/api/model/auth/invite/${encodeURIComponent(sleutel)}`, {
        method: 'POST', body: JSON.stringify({ password: w1 }),
      })
      onKlaar()
    } catch (err) { setFout(err instanceof Error ? err.message : String(err)) } finally { setBezig(false) }
  }
  return (
    <Kaart>
      <Kop />
      {email === null && !fout ? <Loading /> : null}
      {email === null && fout ? <div className="note">{fout}</div> : null}
      {email !== null ? (
        <form onSubmit={verstuur}>
          <div className="mut2" style={{ fontSize: 12.5, marginBottom: 10 }}>
            Choose a password for <b>{email}</b>. Use at least 10 characters with upper- and lowercase
            letters and digits.
          </div>
          <div className="field"><label>Password</label>
            <input className="inp" type="password" autoComplete="new-password" value={w1} onChange={(e) => setW1(e.target.value)} /></div>
          <div className="field"><label>Repeat password</label>
            <input className="inp" type="password" autoComplete="new-password" value={w2} onChange={(e) => setW2(e.target.value)} /></div>
          {fout ? <div className="note" style={{ marginBottom: 8 }}>{fout}</div> : null}
          <button className="btn primary" type="submit" disabled={bezig || !w1 || !w2} style={{ width: '100%' }}>
            Set password
          </button>
        </form>
      ) : null}
    </Kaart>
  )
}

interface VolgersData {
  profiles: { key: string; name: string; platform: string; new_followers: number; current: number }[]
  series: { profile: string; date: string; followers: number }[]
  totals: { new_followers: number; current: number }
}

/** Groei van de eigen volgers, per profiel te filteren (§4.2). */
function Volgers() {
  const [dagen, setDagen] = useState('30')
  const [profiel, setProfiel] = useState('all')
  const [data, setData] = useState<VolgersData | null>(null)
  const [fout, setFout] = useState('')
  useEffect(() => {
    setData(null)
    portalVraag<VolgersData>(`/api/model/followers?days=${dagen}`).then(setData)
      .catch((e) => setFout(String(e.message || e)))
  }, [dagen])
  const naamVan = (k: string) => {
    const p = data?.profiles.find((x) => x.key === k)
    return p ? `${p.name} (${PLATFORM_NAAM[p.platform] ?? p.platform})` : k
  }
  const zichtbaar = (data?.profiles ?? []).filter((p) => profiel === 'all' || p.key === profiel)
  const labels = [...new Set((data?.series ?? []).map((r) => r.date))].sort()
  const reeksen = zichtbaar.map((p) => {
    const per = new Map((data?.series ?? []).filter((r) => r.profile === p.key).map((r) => [r.date, r.followers]))
    let vorige = 0
    return { naam: naamVan(p.key), waarden: labels.map((d) => (vorige = per.get(d) ?? vorige)) }
  })
  return (
    <Panel title="Followers" right={
      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        <select className="select" value={profiel} onChange={(e) => setProfiel(e.target.value)}>
          <option value="all">All profiles</option>
          {(data?.profiles ?? []).map((p) => <option key={p.key} value={p.key}>{naamVan(p.key)}</option>)}
        </select>
        {VENSTERS.slice(0, 2).map((v) => (
          <button key={v.value} type="button" className={dagen === v.value ? 'btn primary sm' : 'btn ghost sm'}
                  onClick={() => setDagen(v.value)}>{v.label}</button>
        ))}
      </div>
    }>
      {fout ? <ErrorNote error={fout} /> : data === null ? <Loading /> : zichtbaar.length === 0 ? (
        <div className="mut2">No follower data yet.</div>
      ) : (
        <>
          <div className="row" style={{ gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
            {zichtbaar.map((p) => (
              <div key={p.key} style={{ fontSize: 13 }}>
                <b>{naamVan(p.key)}</b> · {p.current.toLocaleString()} followers ·{' '}
                <span style={{ color: 'var(--ok)' }}>+{p.new_followers}</span>
              </div>
            ))}
          </div>
          <LineChartPanel labels={labels} reeksen={reeksen} gevuld={reeksen.length === 1} />
        </>
      )}
    </Panel>
  )
}

interface ContentRegel { title: string; platform: string; price: number | null; currency: string; status: string; date: string | null }

const STATUS_TEKST: Record<string, string> = { for_sale: 'For sale', scheduled: 'Scheduled', not_for_sale: 'Not for sale' }

/** Betaalde posts van de bot: prijs en of ze nu te koop staan (§4.3), geen verkoopaantal. */
function ContentTeKoop() {
  const [items, setItems] = useState<ContentRegel[] | null>(null)
  const [fout, setFout] = useState('')
  useEffect(() => {
    portalVraag<{ items: ContentRegel[] }>('/api/model/content').then((r) => setItems(r.items))
      .catch((e) => setFout(String(e.message || e)))
  }, [])
  return (
    <Panel title="Content for sale" flush>
      {fout ? <ErrorNote error={fout} /> : items === null ? <Loading /> : (
        <Table cols={[
          { key: 't', label: 'Post' }, { key: 'p', label: 'Platform' }, { key: 'pr', label: 'Price', num: true },
          { key: 's', label: 'Status' }, { key: 'd', label: 'Date' },
        ]} emptyText="No paid posts yet.">
          {items.map((r, i) => (
            <tr key={i}>
              <td>{r.title || <span className="mut">—</span>}</td>
              <td>{PLATFORM_NAAM[r.platform] ?? r.platform}</td>
              <td className="num mono">{r.price == null ? '—' : `${r.currency === 'USD' ? '$' : '€'}${r.price.toFixed(2)}`}</td>
              <td>{STATUS_TEKST[r.status] ?? r.status}</td>
              <td className="mono">{r.date ? new Date(r.date + 'Z').toLocaleDateString() : '—'}</td>
            </tr>
          ))}
        </Table>
      )}
    </Panel>
  )
}

const VENSTERS = [{ value: '30', label: '30 days' }, { value: '90', label: '90 days' }, { value: '365', label: 'Year' }]

function vandaag(): string { return new Date().toISOString().slice(0, 10) }
function maandBegin(): string { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1, 12).toISOString().slice(0, 10) }

function Overzicht({ onUit }: { onUit: () => void }) {
  const [ik, setIk] = useState<Ik | null>(null)
  const [dagen, setDagen] = useState('30')
  const [verdiensten, setVerdiensten] = useState<Record<string, PerPlatform> | null>(null)
  const [regels, setRegels] = useState<Regel[] | null>(null)
  const [fout, setFout] = useState('')
  const [van, setVan] = useState(maandBegin())
  const [tot, setTot] = useState(vandaag())
  const [tfa, setTfa] = useState<{ secret: string; otpauth_uri: string } | null>(null)
  const [code, setCode] = useState('')
  const [tfaFout, setTfaFout] = useState('')

  useEffect(() => { portalVraag<Ik>('/api/model/me').then(setIk).catch((e) => setFout(String(e.message || e))) }, [])
  useEffect(() => {
    setVerdiensten(null); setRegels(null)
    if (!ik?.earnings_enabled) return
    portalVraag<{ per_platform: Record<string, PerPlatform> }>(`/api/model/earnings?days=${dagen}`)
      .then((r) => setVerdiensten(r.per_platform)).catch((e) => setFout(String(e.message || e)))
    portalVraag<{ items: Regel[] }>(`/api/model/transactions?days=${dagen}`)
      .then((r) => setRegels(r.items)).catch((e) => setFout(String(e.message || e)))
  }, [dagen, ik?.earnings_enabled])
  const platforms = useMemo(() => Object.entries(verdiensten ?? {}), [verdiensten])

  async function tfaStart() {
    setTfaFout('')
    try { setTfa(await portalVraag('/api/model/2fa/start', { method: 'POST' })) } catch (e) { setTfaFout(String((e as Error).message)) }
  }
  async function tfaBevestig() {
    setTfaFout('')
    try {
      await portalVraag('/api/model/2fa/confirm', { method: 'POST', body: JSON.stringify({ code }) })
      setTfa(null); setCode(''); setIk((x) => (x ? { ...x, two_factor_enabled: true } : x))
    } catch (e) { setTfaFout(String((e as Error).message)) }
  }
  async function tfaUit() {
    setTfaFout('')
    try {
      await portalVraag('/api/model/2fa/disable', { method: 'POST', body: JSON.stringify({ code }) })
      setCode(''); setIk((x) => (x ? { ...x, two_factor_enabled: false } : x))
    } catch (e) { setTfaFout(String((e as Error).message)) }
  }

  if (fout && !ik) return <Kaart><Kop /><ErrorNote error={fout} /></Kaart>
  if (!ik) return <Kaart><Kop /><Loading /></Kaart>
  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <Kop />
        <div className="row" style={{ gap: 8 }}>
          <span className="mut2" style={{ fontSize: 12.5 }}>{ik.name} · {ik.email}</span>
          <button className="btn ghost sm" type="button" onClick={() => { wisPortalToken(); onUit() }}>Sign out</button>
        </div>
      </div>

      <div className="note" style={{ marginBottom: 12 }}>{ik.payout_note}</div>

      {ik.earnings_enabled ? (
        <>
      <Panel title="Your earnings" right={
        <div className="row" style={{ gap: 6 }}>
          {VENSTERS.map((v) => (
            <button key={v.value} type="button" className={dagen === v.value ? 'btn primary sm' : 'btn ghost sm'}
                    onClick={() => setDagen(v.value)}>{v.label}</button>
          ))}
        </div>
      }>
        {verdiensten === null ? <Loading /> : platforms.length === 0 ? (
          <div className="mut2">No sales in this period.</div>
        ) : (
          <div className="cols" style={{ gridTemplateColumns: `repeat(${platforms.length}, 1fr)`, gap: 10 }}>
            {platforms.map(([p, v]) => (
              <div className="card" key={p}>
                <div className="mut2" style={{ fontSize: 12 }}>{PLATFORM_NAAM[p] ?? p}</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>
                  {v.your_share == null ? 'Not yet known' : eur(v.your_share)}
                </div>
                <div className="mut2" style={{ fontSize: 12 }}>
                  {p === 'fancentro' || p === 'fc' ? 'your share (paid by FanCentro)' : 'your share'}
                  {v.share_pct != null ? ` (${v.share_pct}%)` : ''} · {v.sales} sales · {eur(v.gross)} gross
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Transactions" flush right={
        <div className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="mut2" style={{ fontSize: 12 }}>Export</span>
          <input className="inp sm" type="date" value={van} onChange={(e) => setVan(e.target.value)} />
          <input className="inp sm" type="date" value={tot} onChange={(e) => setTot(e.target.value)} />
          <button className="btn ghost sm" type="button"
                  onClick={() => downloadExport(van, tot).catch((e) => setFout(String(e.message || e)))}>
            Download CSV
          </button>
        </div>
      }>
        {regels === null ? <Loading /> : (
          <Table cols={[
            { key: 'd', label: 'Date' }, { key: 'p', label: 'Platform' }, { key: 't', label: 'Type' },
            { key: 'g', label: 'Gross', num: true }, { key: 's', label: 'Your share', num: true },
          ]} emptyText="No sales in this period.">
            {regels.map((r, i) => (
              <tr key={i}>
                <td className="mono">{r.date ? new Date(r.date + 'Z').toLocaleString() : '—'}</td>
                <td>{PLATFORM_NAAM[r.platform] ?? r.platform}</td>
                <td>{r.type}</td>
                <td className="num mono">{eur(r.gross)}</td>
                <td className="num mono">{r.your_share == null ? <span className="mut">Not yet known</span> : eur(r.your_share)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
        </>
      ) : (
        <Panel title="Your earnings">
          <div className="mut2">Your earnings overview will appear here soon.</div>
        </Panel>
      )}

      <Volgers />

      <ContentTeKoop />

      <Panel title="Security">
        {ik.two_factor_enabled ? (
          <div>
            <div style={{ marginBottom: 8 }}>Two-factor authentication is <b>on</b>.</div>
            <div className="row" style={{ gap: 6 }}>
              <input className="inp sm" inputMode="numeric" placeholder="Code" value={code} onChange={(e) => setCode(e.target.value)} />
              <button className="btn ghost sm" type="button" onClick={tfaUit} disabled={!code}>Turn off</button>
            </div>
          </div>
        ) : tfa ? (
          <div>
            <div className="mut2" style={{ fontSize: 12.5, marginBottom: 6 }}>
              Add this key to your authenticator app, then enter the 6-digit code.
            </div>
            <div className="mono" style={{ marginBottom: 6, wordBreak: 'break-all' }}>{tfa.secret}</div>
            <a className="mut2" style={{ fontSize: 12 }} href={tfa.otpauth_uri}>Open in authenticator app</a>
            <div className="row" style={{ gap: 6, marginTop: 8 }}>
              <input className="inp sm" inputMode="numeric" placeholder="Code" value={code} onChange={(e) => setCode(e.target.value)} />
              <button className="btn primary sm" type="button" onClick={tfaBevestig} disabled={!code}>Confirm</button>
            </div>
          </div>
        ) : (
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <span>Two-factor authentication is off.</span>
            <button className="btn ghost sm" type="button" onClick={tfaStart}>Turn on</button>
          </div>
        )}
        {tfaFout ? <div className="note" style={{ marginTop: 8 }}>{tfaFout}</div> : null}
      </Panel>
    </div>
  )
}

export function PortalApp() {
  const pad = window.location.pathname
  const uitnodiging = pad.match(/^\/portal\/invite\/([^/]+)/)
  const [ingelogd, setIngelogd] = useState(() => !!portalToken())
  if (uitnodiging) {
    return <Uitnodiging sleutel={decodeURIComponent(uitnodiging[1])}
                        onKlaar={() => { window.history.replaceState(null, '', '/portal'); setIngelogd(false); window.location.reload() }} />
  }
  if (!ingelogd) return <Inloggen onIn={() => setIngelogd(true)} />
  return <Overzicht onUit={() => setIngelogd(false)} />
}
